/**
 * Tech Features Custom Component
 * 
 * Custom element that handles synchronization between hotspots and accordions/carousel for
 * the tech features custom section.
 */
class TechFeatures extends HTMLElement {
  connectedCallback() {
    this.hotspots = this.querySelectorAll('.tech-features__hotspot');
    this.accordions = this.querySelectorAll('.tech-features__accordion');
    this.carousel = this.querySelector('scroll-carousel');
    this.carouselItems = this.carousel ? Array.from(this.carousel.querySelectorAll('.tech-features__carousel-item')) : [];
    
    this.init();
  }

  init() {
    // Add click listeners to hotspots
    this.hotspots.forEach(hotspot => {
      hotspot.addEventListener('click', (e) => this.handleHotspotClick(e));
    });

    // Add click listeners to accordion details toggle events
    this.accordions.forEach(accordion => {
      const details = accordion.querySelector('details');
      if (details) {
        details.addEventListener('toggle', () => this.handleAccordionToggle(accordion));
      }
    });

    // Add listener for carousel changes
    if (this.carousel) {
      this.carousel.addEventListener('carousel:change', (e) => this.handleCarouselChange(e));
    }
  }

  handleHotspotClick(event) {
    const hotspot = event.currentTarget;
    const featureId = hotspot.getAttribute('data-feature-id');
    
    // Find corresponding accordion and carousel item
    const targetAccordion = this.querySelector(
      `.tech-features__accordion[data-feature-id="${featureId}"]`
    );
    const targetCarouselItem = this.carousel ? 
      this.carouselItems.find(item => item.getAttribute('data-feature-id') === featureId) : null;

    const isCurrentlyActive = hotspot.classList.contains('is-active');

    // Close all accordions and deactivate all hotspots
    this.closeAllAccordions();
    this.deactivateAllHotspots();

    // If the clicked hotspot wasn't active, activate it
    if (!isCurrentlyActive) {
      hotspot.classList.add('is-active');
      hotspot.setAttribute('aria-expanded', 'true');
      
      // Open the accordion (desktop)
      if (targetAccordion) {
        const details = targetAccordion.querySelector('details');
        if (details && !details.open) {
          targetAccordion.open({ instant: false });
        }
      }

      // Scroll carousel to item (mobile)
      if (this.carousel && targetCarouselItem) {
        const itemIndex = this.carouselItems.indexOf(targetCarouselItem);
        if (itemIndex !== -1) {
          this.carousel.select(itemIndex, { instant: false });
          // Update carousel item active state immediately
          this.updateCarouselActiveState(targetCarouselItem);
        }
      }
    }
  }

  handleAccordionToggle(accordion) {
    const featureId = accordion.getAttribute('data-feature-id');
    const details = accordion.querySelector('details');
    
    // Find corresponding hotspot
    const targetHotspot = this.querySelector(
      `.tech-features__hotspot[data-feature-id="${featureId}"]`
    );

    if (!targetHotspot) return;

    // Check if accordion is now open (after toggle)
    const isOpen = details.open;

    if (isOpen) {
      // Close all other accordions
      this.accordions.forEach(otherAccordion => {
        if (otherAccordion !== accordion) {
          const otherDetails = otherAccordion.querySelector('details');
          if (otherDetails && otherDetails.open) {
            otherAccordion.close();
          }
        }
      });

      // Deactivate all hotspots, then activate this one
      this.deactivateAllHotspots();
      targetHotspot.classList.add('is-active');
      targetHotspot.setAttribute('aria-expanded', 'true');
    } else {
      // Accordion was closed, deactivate its hotspot
      targetHotspot.classList.remove('is-active');
      targetHotspot.setAttribute('aria-expanded', 'false');
    }
  }

  handleCarouselChange(event) {
    const { index, cell } = event.detail;
    const featureId = cell.getAttribute('data-feature-id');
    
    // Find corresponding hotspot
    const targetHotspot = this.querySelector(
      `.tech-features__hotspot[data-feature-id="${featureId}"]`
    );

    if (!targetHotspot) return;

    // Deactivate all hotspots, then activate the one for this carousel item
    this.deactivateAllHotspots();
    targetHotspot.classList.add('is-active');
    targetHotspot.setAttribute('aria-expanded', 'true');

    // Update carousel item active states
    this.updateCarouselActiveState(cell);
  }

  closeAllAccordions() {
    this.accordions.forEach(accordion => {
      const details = accordion.querySelector('details');
      if (details && details.open) {
        accordion.close();
      }
    });
  }

  deactivateAllHotspots() {
    this.hotspots.forEach(hotspot => {
      hotspot.classList.remove('is-active');
      hotspot.setAttribute('aria-expanded', 'false');
    });
  }

  updateCarouselActiveState(activeItem) {
    // Remove active class from all carousel items
    this.carouselItems.forEach(item => {
      item.classList.remove('is-active');
    });
    
    // Add active class to the current item
    if (activeItem) {
      activeItem.classList.add('is-active');
    }
  }
}

if (!window.customElements.get('tech-features')) {
  window.customElements.define('tech-features', TechFeatures);
}

